import React, { useState } from "react";

// Modal is given the setSecret action ref by the parent — this keeps
// the Convex hook ownership in CredentialsVaultTab (one useAction
// registration site per action) and lets us pass a vi.fn() through
// from tests without a second mock surface.

interface AddSecretModalProps {
  setSecret: (args: { name: string; plaintext: string }) => Promise<unknown>;
  onClose: () => void;
  onCreated: () => void;
}

const SECRET_NAME_RE = /^[A-Za-z0-9_.-]{1,128}$/;

export function AddSecretModal({ setSecret, onClose, onCreated }: AddSecretModalProps) {
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Clear plaintext before unmounting so the React closure doesn't
  // keep the secret alive in memory longer than necessary.
  function handleClose() {
    setValue("");
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Secret name is required");
      return;
    }
    if (!SECRET_NAME_RE.test(trimmedName)) {
      setError("Name must be 1–128 chars of [A-Za-z0-9_.-]");
      return;
    }
    if (!value.trim()) {
      setError("Secret value is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await setSecret({ name: trimmedName, plaintext: value });
      onCreated();
      handleClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to create secret";
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="sr-modal-backdrop" role="dialog" aria-modal="true" aria-label="Add secret">
      <div className="sr-modal">
        <div className="sr-modal-header">
          <h2 className="sr-modal-title">Add secret</h2>
          <button className="sr-modal-close" onClick={handleClose} aria-label="Close">
            ✕
          </button>
        </div>
        <form className="sr-modal-body" onSubmit={handleSubmit}>
          <div className="sr-modal-field">
            <label htmlFor="secret-name" className="sr-modal-label">
              Name
            </label>
            <input
              id="secret-name"
              className="sr-modal-input"
              type="text"
              placeholder="e.g. ANTHROPIC_API_KEY"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="sr-modal-field">
            <label htmlFor="secret-value" className="sr-modal-label">
              Value
            </label>
            <input
              id="secret-value"
              className="sr-modal-input"
              type="password"
              placeholder="Paste secret value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoComplete="new-password"
              spellCheck={false}
            />
          </div>
          {error && <p className="sr-modal-error">{error}</p>}
          <div className="sr-modal-actions">
            <button
              type="button"
              className="sr-modal-btn sr-modal-btn--secondary"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="sr-modal-btn sr-modal-btn--primary" disabled={saving}>
              {saving ? "Saving…" : "Add secret"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
